import React from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useLocation, matchRoutes, matchPath } from "react-router-dom";
import { useSelector } from "react-redux";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import { Logger } from "@base-provider";
import getStyled from "../../hooks/getStyled";
import { SFKeyboardArrowRightIcon } from "../icons";
import Breadcrumb from "./breadcrumb";
import SFTooltip from "../tooltip";

const { styled } = getStyled();

const BreadcrumbsStyled = styled(Breadcrumbs)(({ theme }) => ({
  "& .MuiBreadcrumbs-li": {
    display: "flex",
  },
  "& .MuiBreadcrumbs-separator": {
    marginLeft: 5,
    marginRight: 5,
    "& svg": {
      fontSize: "15px",
    },
  },
}));

function flat(array) {
  var result = [];
  array.forEach(function (a) {
    result.push(a);
    if (Array.isArray(a.children)) {
      result = result.concat(flat(a.children));
    }
  });
  return result;
}

const SFBreadcrumbs = ({ routes }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const layoutState = useSelector((state) => state.layout);
  //matchedRoutes is an array of all the routes that match the current location
  //the first route is the very first parent route, the second its child and so on
  //the last route is the current location route
  const matchedRoutes = matchRoutes(routes, location.pathname);
  const startingMatchRoute = matchedRoutes[0].route;
  //get the current route
  //the current route is always the last one in the matchedRoutes array
  const currentRoute = matchedRoutes[matchedRoutes.length - 1].route;
  //get the current route params
  //these will be used to replace the dynamic parts of the route path when using the breadcrumbs
  const currentRouteParams = matchPath(
    { path: currentRoute.path },
    location.pathname
  ).params;
  //this function is used to flatten the routes array
  let flattenRoutes = flat([startingMatchRoute]);
  //get all the routes that match the current location
  //in this way we find the routes that are part of the breadcrumbs
  //routes that we want to display in the breadcrumbs have a label or dynamicBreadcrumb property
  let breadcrumbRoutes = flattenRoutes.filter((route) => {
    return (
      currentRoute.path.includes(route.path) &&
      (route.label || route.dynamicBreadcrumb)
    );
  });

  Logger.of("App.Core.Breadcrumbs").info({
    routes,
    breadcrumbRoutes,
    currentRouteParams,
    currentRoute,
    matchedRoutes,
  });

  //disableParentBreadcrumb means that we don't want to show the parent breadcrumb of the current route
  if (currentRoute.disableParentBreadcrumb) {
    let parentRoute = matchedRoutes[matchedRoutes.length - 2].route;
    breadcrumbRoutes = breadcrumbRoutes.filter(
      (x) => x.path !== parentRoute.path
    );
  }
  //dsableBreadcrumb means that we don't want to show the current route in the breadcrumb
  //this is useful when the current route has label property but we don't want to show it in the breadcrumb
  if (currentRoute.disableBreadcrumb) {
    breadcrumbRoutes = breadcrumbRoutes.filter(
      (x) => x.path !== currentRoute.path
    );
  }

  //in case that the current route is in editing mode
  //we want to show the editing pill in the breadcrumb
  //so we add the editing property to the last route in the breadcrumbRoutes array
  if (currentRoute.editing) {
    breadcrumbRoutes = breadcrumbRoutes.map((x, index) => ({
      ...x,
      editing: index === breadcrumbRoutes.length - 1,
    }));
  }

  const getBreadcrumb = (route) => {
    //if the route has dynamicBreadcrumb property we fetch the breadcrumb from the redux state
    //otherwise we use the label property
    //the dynamicBreadcrumb property is used when we have a dynamic label (eg. a job name) that should be fetched from the redux state
    if (route.dynamicBreadcrumb) {
      let breadcrumb = layoutState.find(
        (x) => x.path === route.path
      )?.breadcrumbName;
      return breadcrumb ?? route.label;
    }
    return route.label;
  };

  return (
    <BreadcrumbsStyled
      separator={<SFKeyboardArrowRightIcon color="grey400" />}
      aria-label={t("components.breadcrumbs.ariaLabel")}
    >
      {breadcrumbRoutes.map((x, index) => {
        let isLastBreadcrumb = index === breadcrumbRoutes.length - 1;
        let label = getBreadcrumb(x);
        //in case that the label is more than 24 characters we truncate it and show a tooltip
        if (label?.length > 24) {
          return (
            <SFTooltip
              key={index}
              placement="bottom-end"
              width="narrow"
              content={label}
            >
              <div style={{ display: "flex" }}>
                <Breadcrumb
                  key={index}
                  isLastBreadcrumb={isLastBreadcrumb}
                  path={x.path}
                  label={label}
                  editing={x.editing}
                  shouldTruncate={true}
                  currentRouteParams={currentRouteParams}
                />
              </div>
            </SFTooltip>
          );
        }
        return (
          <Breadcrumb
            key={index}
            isLastBreadcrumb={isLastBreadcrumb}
            path={x.path}
            label={label}
            editing={x.editing}
            currentRouteParams={currentRouteParams}
          />
        );
      })}
    </BreadcrumbsStyled>
  );
};

SFBreadcrumbs.propTypes = {
  /**
   * Array of all app routes
   */
  routes: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * Route path
       */
      path: PropTypes.string.isRequired,
      /**
       * Route label
       */
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
      /**
       * Route is in editing mode
       */
      editing: PropTypes.bool,
      /**
       * This property is used to not show the parent breadcrumb of the current route
       */
      disableParentBreadcrumb: PropTypes.bool,
      /*
       * This property is used to define that the route has dynamic label (eg. a job name) and should fetch from the redux state
       */
      dynamicBreadcrumb: PropTypes.bool,
    }).isRequired
  ),
};

export default SFBreadcrumbs;
